import { intersection } from "lodash";
import { addDays, parseISO, subDays } from "date-fns";
import { RRule } from "rrule";
import { setWarningSeverity, VisitWarningResourceRequirement, WARNING_SEVERITIES, WarningSeverityValue } from "./visit-resource-warnings";
import { datef } from "../dates";
import { daysOfWeek } from "../client/availability";

export type VisitWarnings = {
  visitId: number;
  severity: WarningSeverityValue;
  resourceRequirementProviderTypes?: {expectedProviderTypes: number[][], actualProviderIds: (number | null)[], extraProviderIds: number[]};
  teamMismatch?: boolean;
  clinicalConstraintViolations?: string[];
}

type VisitWarningResource = {
  providerId: number | null;
  provider: {
    teams: number[];
    providerTypeId: number | null;
    providerType?: {
      id: number;
      name: string;
      requireStateLicense?: boolean;
    } | null;
    traits?: Array<{
      trait: string;
      value: string;
    }> | null;
  } | null
}

export type VisitToCheck = {
  visitId: number;
  startTime?: Date | null;
  endTime?: Date | null;
  patientState?: string | null;
  resources: VisitWarningResource[];
  requirements: VisitWarningResourceRequirement[];
}

export type VisitToCheckForClinicalConstraint = {
  id: number;
  episodeId: number | null;
  careRequirementId: number | null;
  visitTypeId: number;
  startTime: Date;
  endTime: Date;
  addressTimezone?: string | null;
  serviceIds: number[];
}

export type CareRequirementToCheckForClinicalConstraint = {
  id: number;
  episodeId: number;
  visitTypeId: number;
  duration: number;
  /** "daily" | "weekly" for RRule; other values are treated as daily in date logic. */
  rruleFreq: "daily" | "weekly" | string | null;
  rruleDtstart: string | Date | null;
  rruleUntil: string | Date | null;
  rruleInterval: number | null;
  rruleByweekday: string[] | null;
  earliestStartTime: string | null;
  latestStartTime: string | null;
  minTimeFromLastVisit: number | null;
  maxTimeFromLastVisit: number | null;
  serviceIds: number[];
};

export const getVisitWarnings = (visit: VisitToCheck): {warnings: VisitWarnings, expectedProviderTypes: number[][], actualProviderIds: (number | null)[]} => {
  const warnings: VisitWarnings = {
    visitId: visit.visitId,
    severity: WARNING_SEVERITIES.NONE
  }

  const teamsValid = validateVisitResources(visit.resources)

  const { expectedProviderTypes, actualProviderIds } = mapResourcesToRequirements(visit.resources, visit.requirements)

  if (actualProviderIds.filter(Boolean).length != visit.requirements.length || actualProviderIds.length !== visit.resources.length) {
    warnings.resourceRequirementProviderTypes = {
      expectedProviderTypes,
      actualProviderIds,
      extraProviderIds: visit.resources.map(vr => vr.providerId!).filter(providerId => !actualProviderIds.includes(providerId)),
    }
    setWarningSeverity(warnings, WARNING_SEVERITIES.REQUIREMENTS)
  }

  if (!teamsValid) {
    warnings.teamMismatch = true
    setWarningSeverity(warnings, WARNING_SEVERITIES.TEAM)
  }
  
  return { warnings, expectedProviderTypes, actualProviderIds }
}


const validateVisitResources = (resources: VisitWarningResource[]): boolean => {
  // Extract the teamIds from all visitResources
  const allTeamIds: number[][] = resources.map(resource => {
      return resource.provider?.teams || [];
  });

  // Check if all resources have an empty array of teamProviders
  const allEmpty = allTeamIds.every(teamIds => teamIds.length === 0);

  if (allEmpty) return true;

  // Compute the intersection
  const inter = intersection(...allTeamIds)

  // If there's at least one shared teamId, return true
  return inter.length > 0;
}

type ResourceMappingResult = {
  expectedProviderTypes: number[][];
  actualProviderIds: (number | null)[];
}

const mapResourcesToRequirements = (resources: VisitWarningResource[], requirements: VisitWarningResourceRequirement[]): ResourceMappingResult => {
  const actualProviderIds: (number | null)[] = [];

  requirements.forEach((req, index) => {
    const res = resources.find(res => !actualProviderIds.includes(res?.providerId) && req.providerTypeIds.find(pt => res.provider?.providerTypeId === pt));
    actualProviderIds[index] = res?.providerId || null;
  });

  return {
    expectedProviderTypes: requirements.map((r) => r.providerTypeIds),
    actualProviderIds,
  }
}

const CLINICAL_CONSTRAINT_DURATION_TOLERANCE_MINUTES = 5;
const rruleWeekdaysByCode = {
  SU: RRule.SU,
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
} as const;

const dayOfWeekSet = new Set(daysOfWeek);

const addViolation = (warningsByVisit: Map<number, Set<string>>, visitId: number, warning: string) => {
  const current = warningsByVisit.get(visitId) ?? new Set<string>();
  current.add(warning);
  warningsByVisit.set(visitId, current);
}

/** Normalize a date or string to "yyyy-MM-dd" for consistent comparison. Exported for reuse in visit worklist warnings. */
export const getDateKey = (dateOrString: Date | string | null | undefined): string | null => {
  if (!dateOrString) return null;

  if (dateOrString instanceof Date) {
    if (Number.isNaN(dateOrString.getTime())) return null;
    return datef(dateOrString, "yyyy-MM-dd");
  }

  const trimmed = dateOrString.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 10);
}

const isDateKeyAllowedByRRule = (
  visitDateKey: string,
  requirement: CareRequirementToCheckForClinicalConstraint,
): boolean => {
  const reqStartKey = getDateKey(requirement.rruleDtstart);
  const reqUntilKey = getDateKey(requirement.rruleUntil);

  if (reqStartKey && visitDateKey < reqStartKey) return false;
  if (reqUntilKey && visitDateKey > reqUntilKey) return false;

  if (!reqStartKey) return true;

  const startDate = parseISO(reqStartKey);
  const interval = Math.max(1, requirement.rruleInterval ?? 1);
  const visitDate = parseISO(visitDateKey);
  if (Number.isNaN(visitDate.getTime()) || Number.isNaN(startDate.getTime())) return true;

  const freq = requirement.rruleFreq === "weekly" ? RRule.WEEKLY : RRule.DAILY;
  const rawByweekday = requirement.rruleByweekday ?? null;
  const normalizedByweekday = requirement.rruleFreq === "weekly"
    ? (rawByweekday ?? []).filter((weekday): weekday is (typeof daysOfWeek)[number] =>
      dayOfWeekSet.has(weekday as (typeof daysOfWeek)[number]))
    : [];

  if (requirement.rruleFreq === "weekly" && rawByweekday?.length && !normalizedByweekday.length) {
    return false;
  }

  const byweekday = requirement.rruleFreq === "weekly"
    ? (rawByweekday?.length ? normalizedByweekday : daysOfWeek)
      .map((weekday) => rruleWeekdaysByCode[weekday])
    : undefined;

  const rule = new RRule({
    freq,
    dtstart: startDate,
    interval,
    byweekday,
    until: reqUntilKey ? parseISO(reqUntilKey) : undefined,
  });

  const nearbyOccurrences = rule.between(subDays(visitDate, 1), addDays(visitDate, 1), true);
  return nearbyOccurrences.some((occurrence) => datef(occurrence, "yyyy-MM-dd") === visitDateKey);
}

/** List all date keys in [startDateKey, endDateKey]. Exported for reuse in visit worklist warnings. */
export const getDateKeysInRange = (startDateKey: string, endDateKey: string): string[] => {
  const startDate = parseISO(startDateKey);
  const endDate = parseISO(endDateKey);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate > endDate) return [];

  const dateKeys: string[] = [];
  for (let cursor = startDate; cursor <= endDate; cursor = addDays(cursor, 1)) {
    dateKeys.push(datef(cursor, "yyyy-MM-dd"));
  }
  return dateKeys;
}

/** Check if a date key falls within a requirement's rrule. Exported for reuse in visit worklist warnings. */
export const isDateKeyAllowedByRRuleForRequirement = (
  visitDateKey: string,
  requirement: CareRequirementToCheckForClinicalConstraint,
): boolean => isDateKeyAllowedByRRule(visitDateKey, requirement);

/** Expected visit count per day in range for given requirements. Exported for reuse in visit worklist warnings. */
export const getExpectedVisitCountsByDayInRange = (
  requirements: CareRequirementToCheckForClinicalConstraint[],
  startDateKey: string,
  endDateKey: string,
): Map<string, number> => {
  const expectedVisitsByDay = new Map<string, number>();

  requirements.forEach((requirement) => {
    const reqStartKey = getDateKey(requirement.rruleDtstart);
    const reqUntilKey = getDateKey(requirement.rruleUntil);
    const effectiveStartKey = reqStartKey && reqStartKey > startDateKey ? reqStartKey : startDateKey;
    const effectiveEndKey = reqUntilKey && reqUntilKey < endDateKey ? reqUntilKey : endDateKey;
    if (effectiveStartKey > effectiveEndKey) return;

    getDateKeysInRange(effectiveStartKey, effectiveEndKey).forEach((dateKey) => {
      if (!isDateKeyAllowedByRRule(dateKey, requirement)) return;
      expectedVisitsByDay.set(dateKey, (expectedVisitsByDay.get(dateKey) ?? 0) + 1);
    });
  });

  return expectedVisitsByDay;
}

const getEpisodeRequirementDateRange = (
  requirements: CareRequirementToCheckForClinicalConstraint[],
): { startDateKey: string; endDateKey: string } | null => {
  const startDateKeys = requirements
    .map((requirement) => getDateKey(requirement.rruleDtstart))
    .filter((dateKey): dateKey is string => !!dateKey);

  if (!startDateKeys.length) return null;

  const endDateKeys = requirements
    .map((requirement) => getDateKey(requirement.rruleUntil) ?? getDateKey(requirement.rruleDtstart))
    .filter((dateKey): dateKey is string => !!dateKey);

  if (!endDateKeys.length) return null;

  const sortedStartDateKeys = [...startDateKeys].sort();
  const sortedEndDateKeys = [...endDateKeys].sort();

  return {
    startDateKey: sortedStartDateKeys[0],
    endDateKey: sortedEndDateKeys[sortedEndDateKeys.length - 1],
  };
}

const isRequirementExpiredBeforeDateKey = (
  requirement: CareRequirementToCheckForClinicalConstraint,
  dateKey: string,
): boolean => {
  const reqUntilKey = getDateKey(requirement.rruleUntil);
  return !!reqUntilKey && reqUntilKey < dateKey;
}

const isVisitDateAllowedByRRule = (
  visitStart: Date,
  requirement: CareRequirementToCheckForClinicalConstraint,
  timezone?: string | null,
): boolean => {
  const visitDateKey = datef(visitStart, "yyyy-MM-dd", timezone || undefined);
  return isDateKeyAllowedByRRule(visitDateKey, requirement);
}

const isVisitStartTimeAllowed = (
  visitStart: Date,
  requirement: CareRequirementToCheckForClinicalConstraint,
  timezone?: string | null,
): boolean => {
  const earliest = requirement.earliestStartTime;
  const latest = requirement.latestStartTime;
  if (!earliest && !latest) return true;

  const visitTime = datef(visitStart, "HH:mm:ss", timezone || undefined);
  if (earliest && latest && earliest > latest) {
    return visitTime >= earliest || visitTime <= latest;
  }
  if (earliest && visitTime < earliest) return false;
  if (latest && visitTime > latest) return false;
  return true;
}

const formatMinutesAsHours = (minutes: number): string => {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
}

type RequirementScheduleType = "timeRange" | "previousVisitRelative";

const getRequirementScheduleType = (
  requirement: CareRequirementToCheckForClinicalConstraint,
): RequirementScheduleType => {
  const hasTimeRange =
    requirement.earliestStartTime !== null && requirement.earliestStartTime !== undefined
      ? true
      : requirement.latestStartTime !== null && requirement.latestStartTime !== undefined;
  const hasRelative =
    requirement.minTimeFromLastVisit !== null && requirement.minTimeFromLastVisit !== undefined
      ? true
      : requirement.maxTimeFromLastVisit !== null && requirement.maxTimeFromLastVisit !== undefined;
  const relativeNonDefault =
    (requirement.minTimeFromLastVisit ?? 0) !== 0 ||
    (requirement.maxTimeFromLastVisit ?? 24 * 60) !== 24 * 60;

  if (hasRelative && !hasTimeRange) return "previousVisitRelative";
  if (hasTimeRange && !hasRelative) return "timeRange";
  if (hasRelative && hasTimeRange) return relativeNonDefault ? "previousVisitRelative" : "timeRange";
  return "timeRange";
}

const validateVisitAgainstRequirement = (
  visit: VisitToCheckForClinicalConstraint,
  requirement: CareRequirementToCheckForClinicalConstraint,
  previousVisit: VisitToCheckForClinicalConstraint | undefined,
): string[] => {
  const violations: string[] = [];

  if (visit.visitTypeId !== requirement.visitTypeId) {
    violations.push("Visit type does not match clinical constraint requirement for this position in the day schedule.");
  }

  const actualDurationInMinutes = Math.round((visit.endTime.getTime() - visit.startTime.getTime()) / 60000);
  if (Math.abs(actualDurationInMinutes - requirement.duration) > CLINICAL_CONSTRAINT_DURATION_TOLERANCE_MINUTES) {
    violations.push(
      `Visit duration is ${actualDurationInMinutes} minutes; expected ${requirement.duration} minutes by clinical constraint.`,
    );
  }

  if (requirement.serviceIds.length) {
    const missingServiceIds = requirement.serviceIds.filter((serviceId) => !visit.serviceIds.includes(serviceId));
    if (missingServiceIds.length) {
      violations.push(
        `Visit is missing ${missingServiceIds.length} required clinical-constraint service${missingServiceIds.length === 1 ? "" : "s"}.`,
      );
    }
  }

  const scheduleType = getRequirementScheduleType(requirement);
  if (scheduleType === "timeRange") {
    if (!isVisitStartTimeAllowed(visit.startTime, requirement, visit.addressTimezone)) {
      violations.push("Visit start time is outside the clinical constraint time window.");
    }
    return violations;
  }

  if (!previousVisit) {
    violations.push("Visit is configured as relative-to-last-visit, but there is no previous visit in this episode.");
    return violations;
  }

  const minutesFromPreviousVisit = (visit.startTime.getTime() - previousVisit.startTime.getTime()) / 60000;
  const minMinutes = requirement.minTimeFromLastVisit ?? 0;
  const maxMinutes = requirement.maxTimeFromLastVisit ?? 24 * 60;
  const diffInHours = formatMinutesAsHours(minutesFromPreviousVisit);

  if (minutesFromPreviousVisit < minMinutes) {
    violations.push(
      `Visit starts ${diffInHours} hours after previous visit; minimum is ${formatMinutesAsHours(minMinutes)} hours.`,
    );
  }

  if (minutesFromPreviousVisit > maxMinutes) {
    violations.push(
      `Visit starts ${diffInHours} hours after previous visit; maximum is ${formatMinutesAsHours(maxMinutes)} hours.`,
    );
  }

  return violations;
}

/**
 * Validates visits against episode care requirements (clinical constraints).
 * Groups by episode, then by day; for each day requirement, validates the matched visit
 * (by careRequirementId) for visit type, duration, services, time window, and relative spacing.
 * Returns a map of visit id → list of violation messages. Days outside the time window are not 
 * checked for missing visits, because their visits were never loaded.
 */
export const getClinicalConstraintViolationsByVisit = (
  visits: VisitToCheckForClinicalConstraint[],
  careRequirements: CareRequirementToCheckForClinicalConstraint[],
  window: { start: Date; end: Date },
): Map<number, string[]> => {
  console.log(
    `[Visit validation] Checking ${visits.length} visit(s) against ${careRequirements.length} care requirement(s).`,
  );
  
  const warningsByVisit = new Map<number, Set<string>>();
  if (!visits.length) {
    console.log("[Visit validation] No visits in this batch.");
    return new Map<number, string[]>();
  }

  const careRequirementsByEpisodeId = new Map<number, CareRequirementToCheckForClinicalConstraint[]>();

  careRequirements.forEach((requirement) => {
    const episodeRequirements = careRequirementsByEpisodeId.get(requirement.episodeId) ?? [];
    episodeRequirements.push(requirement);
    careRequirementsByEpisodeId.set(requirement.episodeId, episodeRequirements);
  });

  const sortedVisits = [...visits].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const visitsByEpisodeId = new Map<number, VisitToCheckForClinicalConstraint[]>();

  sortedVisits.forEach((visit) => {
    if (!visit.episodeId) {
      console.warn(
        `[Visit validation] Visit ${visit.id} is missing an episode. Added a validation warning.`,
      );
      addViolation(
        warningsByVisit,
        visit.id,
        "Visit is not linked to an episode and cannot be validated against clinical constraints.",
      );
      return;
    }

    const episodeVisits = visitsByEpisodeId.get(visit.episodeId) ?? [];
    episodeVisits.push(visit);
    visitsByEpisodeId.set(visit.episodeId, episodeVisits);
  });

  visitsByEpisodeId.forEach((episodeVisits, episodeId) => {
    const allEpisodeRequirements = [...(careRequirementsByEpisodeId.get(episodeId) ?? [])]
      .sort((a, b) => a.id - b.id);

    const validationWindowStartDateKey = datef(
      episodeVisits[0].startTime,
      "yyyy-MM-dd",
      episodeVisits[0].addressTimezone || undefined,
    );
    const episodeRequirements = allEpisodeRequirements.filter(
      (requirement) => !isRequirementExpiredBeforeDateKey(requirement, validationWindowStartDateKey),
    );

    if (!episodeRequirements.length) return;

    const previousVisitById = new Map<number, VisitToCheckForClinicalConstraint | undefined>();
    let previousVisit: VisitToCheckForClinicalConstraint | undefined;
    episodeVisits.forEach((visit) => {
      previousVisitById.set(visit.id, previousVisit);
      previousVisit = visit;
    });

    // Visits are already globally sorted by date/time, so each day group preserves time order.
    const visitsByDay = new Map<string, VisitToCheckForClinicalConstraint[]>();
    episodeVisits.forEach((visit) => {
      const dayKey = datef(visit.startTime, "yyyy-MM-dd", visit.addressTimezone || undefined);
      const dayVisits = visitsByDay.get(dayKey) ?? [];
      dayVisits.push(visit);
      visitsByDay.set(dayKey, dayVisits);
    });

    const episodeRequirementDateRange = getEpisodeRequirementDateRange(episodeRequirements);
    if (episodeRequirementDateRange) {
      // Only days inside the loaded window can be judged to have no visits.
      const timezone = episodeVisits[0].addressTimezone || undefined;
      const windowStartDateKey = datef(window.start, "yyyy-MM-dd", timezone);
      const windowEndDateKey = datef(window.end, "yyyy-MM-dd", timezone);
      const startDateKey = windowStartDateKey > episodeRequirementDateRange.startDateKey
        ? windowStartDateKey
        : episodeRequirementDateRange.startDateKey;
      const endDateKey = windowEndDateKey < episodeRequirementDateRange.endDateKey
        ? windowEndDateKey
        : episodeRequirementDateRange.endDateKey;
      const expectedVisitCountsByDay = startDateKey <= endDateKey
        ? getExpectedVisitCountsByDayInRange(episodeRequirements, startDateKey, endDateKey)
        : new Map<string, number>();
      const episodeAnchorVisitId = episodeVisits[0].id;
      expectedVisitCountsByDay.forEach((expectedVisitsCount, dayKey) => {
        if (visitsByDay.has(dayKey)) return;
        addViolation(
          warningsByVisit,
          episodeAnchorVisitId,
          `Clinical constraints expect ${expectedVisitsCount} visit(s) on ${dayKey}, but found 0.`,
        );
      });
    }

    visitsByDay.forEach((dayVisits, dayKey) => {
      const dayAnchorVisit = dayVisits[0];
      const dayRequirements = episodeRequirements.filter((requirement) =>
        isVisitDateAllowedByRRule(dayAnchorVisit.startTime, requirement, dayAnchorVisit.addressTimezone)
      );

      if (!dayRequirements.length) {
        // Ignore days that do not have applicable requirements.
        return;
      }

      if (dayVisits.length !== dayRequirements.length) {
        addViolation(
          warningsByVisit,
          dayVisits[0].id,
          `Clinical constraints expect ${dayRequirements.length} visit(s) on ${dayKey}, but found ${dayVisits.length}.`,
        );
      }

      const dayVisitsByRequirementId = new Map<number, VisitToCheckForClinicalConstraint[]>();
      dayVisits.forEach((visit) => {
        if (visit.careRequirementId == null) return;
        const requirementVisits = dayVisitsByRequirementId.get(visit.careRequirementId) ?? [];
        requirementVisits.push(visit);
        dayVisitsByRequirementId.set(visit.careRequirementId, requirementVisits);
      });

      dayRequirements.forEach((requirement) => {
        const matchedVisits = dayVisitsByRequirementId.get(requirement.id);
        if (!matchedVisits?.length) {
          // Missing-day/mismatch warnings are already handled at the day/date-range level.
          return;
        }

        // Clinical-constraint validation assumes one visit per requirement per day.
        const primaryVisit = matchedVisits[0];
        const visitViolations = validateVisitAgainstRequirement(
          primaryVisit,
          requirement,
          previousVisitById.get(primaryVisit.id),
        );

        visitViolations.forEach((warning) => addViolation(warningsByVisit, primaryVisit.id, warning));
      });
    });
  });

  const result = new Map<number, string[]>();
  warningsByVisit.forEach((warningSet, visitId) => {
    result.set(visitId, [...warningSet]);
  });

  if (result.size) {
    console.log(`[Visit validation] Found issues on ${result.size} visit(s).`);
  } else {
    console.log("[Visit validation] No issues found.");
  }

  return result;
}
